import type { ReviewInstructions } from "@ark-bot/protocol";
import { useEffect, useState } from "react";

import { api } from "../api.js";
import { useText } from "../i18n.js";
import { SETTINGS } from "../text/settings.js";

const toLines = (list: readonly string[]): string => list.join("\n");
const fromLines = (text: string): string[] => text.split("\n").map((line) => line.trim()).filter((line) => line.length > 0);

/**
 * The checks settings, as the prototype's Auto-review instructions: the
 * switch, and the user's standing always-allow and always-block sentences
 * that the reviewer reads with every flagged action. One sentence per line.
 * Settings shows it as its Permissions page, which stays open after a save.
 */
export function ReviewSettings(): React.JSX.Element {
  const t = useText(SETTINGS);
  const [loaded, setLoaded] = useState<ReviewInstructions | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [allow, setAllow] = useState("");
  const [block, setBlock] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .settings()
      .then((settings) => {
        if (cancelled) return;
        setLoaded(settings.review);
        setEnabled(settings.review.enabled);
        setAllow(toLines(settings.review.allow));
        setBlock(toLines(settings.review.block));
      })
      .catch((failure: unknown) => {
        if (!cancelled) setError(failure instanceof Error ? failure.message : String(failure));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const dirty = loaded != null && (enabled !== loaded.enabled || toLines(fromLines(allow)) !== toLines(loaded.allow) || toLines(fromLines(block)) !== toLines(loaded.block));

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (saving || loaded == null) return;
    setSaving(true);
    setError(null);
    try {
      const settings = await api.updateSettings({ review: { enabled, allow: fromLines(allow), block: fromLines(block) } });
      // The host normalizes what it keeps; show that, so the form is what the reviewer reads.
      setLoaded(settings.review);
      setAllow(toLines(settings.review.allow));
      setBlock(toLines(settings.review.block));
      setSaved(true);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="agent-settings review-settings" onSubmit={(event) => void submit(event)}>
      <p className="muted settings-lead">{t.reviewLead}</p>
      <label className="inline">
        <input type="checkbox" checked={enabled} disabled={loaded == null} onChange={(event) => { setEnabled(event.target.checked); setSaved(false); }} />
        {t.reviewEnabled} <span className="muted">{t.reviewEnabledNote}</span>
      </label>
      <label>
        {t.alwaysAllow} <span className="muted">{t.alwaysAllowNote}</span>
        <textarea rows={4} value={allow} disabled={loaded == null} onChange={(event) => { setAllow(event.target.value); setSaved(false); }} placeholder={t.alwaysAllowExample} />
      </label>
      <label>
        {t.alwaysBlock} <span className="muted">{t.alwaysBlockNote}</span>
        <textarea rows={4} value={block} disabled={loaded == null} onChange={(event) => { setBlock(event.target.value); setSaved(false); }} placeholder={t.alwaysBlockExample} />
      </label>
      {error != null ? <div className="settings-error">{error}</div> : null}
      <div className="settings-actions">
        <button type="submit" className="primary" disabled={saving || loaded == null || !dirty}>
          {t.save}
        </button>
        {saved && !dirty ? <span className="muted settings-saved" role="status">{t.saved}</span> : null}
      </div>
    </form>
  );
}
